import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { parseSqlTemporalLiteral } from '../../../../core/utils/sqlTemporalLiteral';
import type { FilterCondition } from '../../core/types/workspaceTypes';
import { CONTAINS_ESCAPE_SRC, containsValueOfPattern } from './containsLikeSql';
import { buildConditionSql } from './filterWhereBuilder';
import { QUALIFIED_IDENT } from './predicateIdentGrammar';
import { parseInListPredicate } from './predicateInListBranch';
import { parseJsonPredicate } from './predicateJsonBranch';
import { parseJsonPredicateByEmit } from './predicateJsonEmitMatch';
import { decodeTextLiteral, textValueSrc } from './predicateJsonHelpers';
import {
  hasTopLevelConnector,
  parseArrayPredicate,
  parseBetweenPredicate,
  parseBooleanLiteral,
  parseNumberLiteral,
  sqlServerTextTypeOf,
  temporalTypeOfLiteral,
  unwrapParentheses,
} from './predicateScalarShapes';
import { exactNumber } from './sqlNumericLiteral';

export type FilterConditionParsed = {
  field: string;
  condition: FilterCondition;
  /**
   * The column type the predicate's own literals were written for — an
   * Oracle `TO_DATE` is a `date` (`sqlTemporalLiteral`). `reemitParsedCondition`
   * uses it when the field resolves to no schema column, so the filter
   * re-applies with the same literals.
   */
  valueDataType?: string;
};

/** Scalar comparison operators that map directly to FilterCondition.operator */
type ScalarOp = '=' | '!=' | '<=' | '>=' | '<' | '>' | 'LIKE';
const SCALAR_OPS: Record<string, ScalarOp> = {
  '=': '=',
  '<>': '!=',
  '!=': '!=',
  '<=': '<=',
  '>=': '>=',
  '<': '<',
  '>': '>',
  LIKE: 'LIKE',
};

/**
 * Unquote a SQL text literal. Returns the bare string value, or `null` if the
 * input is not one. On MySQL this also decodes the `_utf8mb4 X'<hex>'` form
 * `quoteMysqlText` emits for any value containing `\` or NUL — without it a
 * chip built on `name = 'C:\new\test'` re-opens as raw text instead of the
 * structured editor. See `decodeTextLiteral` in `predicateJsonHelpers.ts`.
 */
function unquoteString(raw: string, engine?: DatabaseEngineId): string | null {
  return decodeTextLiteral(raw, engine);
}

/**
 * Returns true if the value side contains a function call or subquery —
 * either a `(` anywhere, or the keyword SELECT.
 */
function valueIsSafe(v: string): boolean {
  return !v.includes('(') && !/\bSELECT\b/i.test(v);
}

/** Scalar comparison operators whose value re-emits as a bare literal when numeric. */
const SCALAR_COMPARISON_OPS: ReadonlySet<FilterCondition['operator']> = new Set([
  '=', '!=', '<', '<=', '>', '>=',
]);

/**
 * FIX-F — preserve the parsed literal's TYPE when the edit chip re-emits a
 * predicate. The chip's value input is typed off the RESOLVED column's data
 * type; when the field resolves to no schema column (a CTE projection alias
 * like `n`, a subquery output, an expression) the input falls back to the
 * untyped text input, which hands back a STRING — and `buildConditionSql`
 * quotes strings. Editing `n < 5` to `3` then emits `n < '3'`: the numeric
 * literal silently degrades to a quoted string (wrong SQL semantics — e.g.
 * SQLite orders every integer below every text, so a recursive step guarded
 * by `n < '3'` never terminates the way `n < 3` does).
 *
 * Rule: the ORIGINAL parsed literal's type governs the re-emit. If the
 * predicate the user opened had a NUMERIC value, the operator is still a
 * scalar comparison, and the new text is a number a JS number holds exactly
 * in any spelling (`exactNumber`: `3`, `+3`, `1e3`), the value is coerced
 * back to a number (→ bare literal); any other number stays text, which the
 * emitter writes bare in plain decimal (`sqlNumericLiteral`). A string-literal
 * original stays a string even when the new text looks numeric — `s = 'a'`
 * edited to `3` emits `s = '3'`, preserving the user's literal form. Each
 * BETWEEN bound follows its own original (`initialValue2` is the upper one;
 * a comparison switched to BETWEEN gives both bounds the value's type), so
 * `n BETWEEN 1 AND 3` edited to 2 and 3 emits `n BETWEEN 2 AND 3`, not
 * `'2' AND '3'`.
 */
export function preserveParsedValueShape(
  initialValue: FilterCondition['value'],
  condition: FilterCondition,
  initialValue2?: FilterCondition['value2'],
): FilterCondition {
  // A list of numbers (`n IN (1, 2)`) keeps its numbers the same way: an
  // untyped list entry hands back text, and a numeric-looking one is a number.
  if (Array.isArray(initialValue) && Array.isArray(condition.value)) {
    if (initialValue.length === 0 || !initialValue.every((v) => typeof v === 'number')) return condition;
    return { ...condition, value: condition.value.map((v) => (typeof v === 'string' ? (exactNumber(v) ?? v) : v)) };
  }
  const asNumber = (initial: unknown, value: unknown): number | null =>
    typeof initial === 'number' && typeof value === 'string' ? exactNumber(value) : null;
  if (condition.operator === 'BETWEEN') {
    const value = asNumber(initialValue, condition.value);
    const value2 = asNumber(initialValue2 ?? initialValue, condition.value2);
    if (value === null && value2 === null) return condition;
    return { ...condition, ...(value !== null ? { value } : {}), ...(value2 !== null ? { value2 } : {}) };
  }
  if (!SCALAR_COMPARISON_OPS.has(condition.operator)) return condition;
  const value = asNumber(initialValue, condition.value);
  return value === null ? condition : { ...condition, value };
}

/**
 * The SQL the edit chip writes when a reopened filter is applied: the
 * predicate's own field text (the user's quoting and alias — never the
 * relation's canonical form), the parsed literal's type kept
 * (`preserveParsedValueShape`), and the compared value's data type for the
 * temporal and exact-number literals — the resolved schema column's
 * (`columnDataType`); on a column the schema does not know (a CTE column, an
 * expression), the type the filter's own literals were written for: an
 * Oracle `TO_DATE` stays `TO_DATE` (as a quoted string it would depend on
 * NLS_DATE_FORMAT), and a numeric filter writes a number too precise for a JS
 * number bare (`sqlNumericLiteral`), like the value it replaces.
 */
export function reemitParsedCondition(
  parsed: FilterConditionParsed,
  condition: FilterCondition,
  engine: DatabaseEngineId,
  options: { readonly columnDataType?: string; readonly quoteSiblingField?: (columnName: string) => string } = {},
): string {
  const { value, value2 } = parsed.condition;
  const numeric =
    typeof value === 'number' ||
    typeof value2 === 'number' ||
    (Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === 'number'));
  return buildConditionSql(parsed.field, preserveParsedValueShape(value, condition, value2), engine, {
    quoteSiblingField: options.quoteSiblingField,
    valueDataType: options.columnDataType || parsed.valueDataType || (numeric ? 'numeric' : undefined),
  });
}

/**
 * The SQL the edit chip commits for a reopened filter (`EditBuilder`): the
 * predicate's own text, byte for byte, when the committed condition writes
 * what the reopened one writes — Apply without a change, including an
 * operator the column's chip upgraded it to (`lookup`, `enum`, the boolean
 * switch) and a value an untyped input handed back as text
 * (`preserveParsedValueShape`). The chip writes a value by the column's type,
 * which the user's own literal need not match — `'abc'` on a SQL Server
 * nvarchar column, `N'東京'` on a varchar one (re-written as `'東京'`, which
 * SQL Server reads as `'??'`), a mixed `N` list, a JSON filter saved in an
 * older form — so re-writing an unchanged filter could change what it
 * matches. Any change writes the condition by the column's type
 * (`reemitParsedCondition`).
 */
export function committedPredicateSql(
  predicate: string,
  parsed: FilterConditionParsed,
  condition: FilterCondition,
  engine: DatabaseEngineId,
  options: Parameters<typeof reemitParsedCondition>[3] = {},
): string {
  const sql = reemitParsedCondition(parsed, condition, engine, options);
  return sql === reemitParsedCondition(parsed, parsed.condition, engine, options) ? predicate : sql;
}

/**
 * On SQL Server, the text type the predicate's literals were written for when
 * the no-type rule would write them otherwise (`sqlServerTextTypeOf`) — so an
 * edited value on a column the schema does not resolve takes the filter's own
 * `N'…'` / `'…'`. JSON filters write their values without the column type.
 */
function withSqlServerTextType(s: string, parsed: FilterConditionParsed): FilterConditionParsed {
  if (parsed.valueDataType || parsed.condition.operator === 'json') return parsed;
  const valueDataType = sqlServerTextTypeOf(s, parsed);
  return valueDataType ? { ...parsed, valueDataType } : parsed;
}

/**
 * Conservative parser: turns a simple SQL predicate string into a
 * `{ field, condition }` pair suitable for the VQE chip editor.
 *
 * Returns `null` for any shape it cannot confidently represent: functions,
 * subqueries and multi-predicate (AND/OR) text the chip did not write.
 *
 * `engine` is OPTIONAL (back-compat: existing single-arg callers/tests are
 * unaffected). It gates the JSON branch and PostgreSQL text casts — a JSON `AND`-guarded /
 * `path_neq`'s-inherent-`OR` shape must be recognized BEFORE the generic
 * AND/OR compound-predicate rejection below, so both the JSON and enum
 * branches run first and return early on a confident match. Without
 * `engine`, the JSON branch is skipped entirely (degrades to raw-text via
 * the final `return null`, never a best-guess chip) — see
 * `predicateJsonBranch.ts` / `predicateJsonBranchPg.ts` for the per-engine
 * shapes inverted, and `predicateInListBranch.ts` for the value lists
 * (`IN (...)` / `NOT IN (...)`, Oracle's split list).
 */
export function predicateToFilterCondition(
  predicate: string,
  engine?: DatabaseEngineId,
): FilterConditionParsed | null {
  const parsed = parsePredicate(predicate.trim(), engine);
  return parsed && engine === 'mssql' ? withSqlServerTextType(predicate.trim(), parsed) : parsed;
}

function parsePredicate(s: string, engine: DatabaseEngineId | undefined): FilterConditionParsed | null {
  if (!s) return null;

  // JSON branch — engine-specific; the emitted shape can itself contain
  // "AND" (declared-on-text guard) / "OR" (`path_neq`), so this MUST run
  // before the generic AND/OR rejection below. Every JSON regex is fully
  // anchored to the emitter's exact shape, so a genuine user-typed compound
  // predicate never false-matches here — it falls through unchanged.
  if (engine) {
    const jsonParsed = parseJsonPredicate(s, engine);
    if (jsonParsed) return jsonParsed;
  }

  // Value lists — `IN (...)` / `NOT IN (...)` and Oracle's split list, which
  // carries its own OR / AND (see predicateInListBranch.ts). `engine` widens
  // the literal spellings (MySQL hex text form) and enables the Oracle shape.
  const listParsed = parseInListPredicate(s, engine);
  if (listParsed) return listParsed;

  // BETWEEN (its own `AND`), the PostgreSQL array operators, and the JSON
  // shapes written as a lateral subquery / `EXISTS` / `COUNT(*)` — see
  // `predicateScalarShapes.ts` and `predicateJsonEmitMatch.ts`.
  const shaped =
    parseBetweenPredicate(s, engine) ??
    parseArrayPredicate(s, engine) ??
    (engine ? parseJsonPredicateByEmit(s, engine) : null);
  if (shaped) return shaped;

  // A multi-part filter the chip wrote, stored inside one pair of parentheses
  // (`(guard AND pred)`, `(x BETWEEN 1 AND 2)`) — the parentheses keep it one
  // condition under a neighbouring OR; the filter is the text inside.
  const inner = unwrapParentheses(s);
  if (inner !== null && hasTopLevelConnector(inner)) return predicateToFilterCondition(inner, engine);

  // Reject compound predicates early
  if (/\bAND\b|\bOR\b/i.test(s)) return null;

  // --- IS [NOT] NULL ---
  const isNullMatch = s.match(
    /^(.+?)\s+IS\s+(NOT\s+)?NULL\s*$/i
  );
  if (isNullMatch) {
    const field = isNullMatch[1].trim();
    if (!QUALIFIED_IDENT.test(field)) return null;
    const operator: FilterCondition['operator'] = isNullMatch[2]
      ? 'IS NOT NULL'
      : 'IS NULL';
    return { field, condition: { operator } };
  }

  // --- CONTAINS: LOWER(<field>) LIKE LOWER('%<value>%') [ESCAPE '!'] ---
  // Must appear BEFORE the generic scalar/LIKE matcher since the field side
  // starts with LOWER( which fails QUALIFIED_IDENT, and the value side
  // contains '(' which fails valueIsSafe.
  // The value side is the whole literal (see `textValueSrc`) — on MySQL that
  // may be the `_utf8mb4 X'<hex>'` form, which the `'(.*)'` shape this used to
  // hard-code could never match, so a CONTAINS chip on a backslash-bearing
  // value dropped to raw text.
  const containsMatch = s.match(
    new RegExp(`^LOWER\\((.+?)\\)\\s+LIKE\\s+LOWER\\(\\s*${textValueSrc(engine)}\\s*\\)${CONTAINS_ESCAPE_SRC}$`, 'i'),
  );
  if (containsMatch) {
    const field = (engine === 'pgsql' ? containsMatch[1].replace(/::text\s*$/i, '') : containsMatch[1]).trim();
    if (!QUALIFIED_IDENT.test(field)) return null;
    // Group 2 is the SQL literal, e.g. `'%test%'`, group 3 the ESCAPE clause.
    // Not a %value% pattern the emitter writes (`containsLikeSql`) — a
    // wildcard of its own inside — can't round-trip as CONTAINS; return null.
    const literalBody = decodeTextLiteral(containsMatch[2], engine);
    if (literalBody === null) return null;
    const value = containsValueOfPattern(literalBody, containsMatch[3], engine ?? 'mock');
    if (value === null) return null;
    return { field, condition: { operator: 'CONTAINS' as FilterCondition['operator'], value } };
  }

  // --- Scalar comparison: <ident> <op> <value> ---
  // Build operator alternation (longest match first to avoid < matching <=)
  const opPattern = /^(.+?)\s*(<=|>=|<>|!=|=|<|>|LIKE)\s*(.+)$/i;
  const opMatch = s.match(opPattern);
  if (opMatch) {
    const rawOp = opMatch[2].toUpperCase() as string;
    const field = (engine === 'pgsql' && rawOp === 'LIKE' ? opMatch[1].replace(/::text\s*$/i, '') : opMatch[1]).trim();
    const valueRaw = opMatch[3].trim();

    // Field must be a plain qualified identifier (no function calls)
    if (!QUALIFIED_IDENT.test(field)) return null;

    const op = SCALAR_OPS[rawOp];
    if (!op) return null;

    // The one call the chip emits as a value: an Oracle temporal literal
    // (`TO_DATE('2024-03-01 00:00:00', 'YYYY-MM-DD HH24:MI:SS')`,
    // `sqlTemporalLiteral`) reads back as the date input's text, so a reopened
    // Oracle date filter stays a structured chip — with the column type it
    // was written for.
    const asTemporal = op === 'LIKE' ? null : parseSqlTemporalLiteral(valueRaw);
    if (asTemporal !== null) {
      return { field, condition: { operator: op, value: asTemporal }, valueDataType: temporalTypeOfLiteral(valueRaw) };
    }

    // Reject function calls / subqueries on the value side
    if (!valueIsSafe(valueRaw)) return null;

    // Try to parse the value as a text literal or number
    const asString = unquoteString(valueRaw, engine);
    if (asString !== null) {
      return { field, condition: { operator: op, value: asString } };
    }

    const asNumber = parseNumberLiteral(valueRaw);
    if (asNumber !== null) {
      return { field, condition: { operator: op, value: asNumber } };
    }

    // `= TRUE` / `= FALSE` — the boolean switch's value on PostgreSQL / MySQL.
    const asBoolean = op === '=' || op === '!=' ? parseBooleanLiteral(valueRaw, engine) : null;
    if (asBoolean !== null) {
      return { field, condition: { operator: op, value: asBoolean } };
    }

    // Unrecognised value shape → bail
    return null;
  }

  return null;
}
